import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ReleaseNoteDetail } from '@/components/release-notes/release-note-detail'
import { Skeleton } from '@/components/ui/skeleton'
import { getReleaseNoteBySlug, getReleaseNotes, type ReleaseNote } from '@/lib/release-notes'

/**
 * Loading state component for release note detail
 *
 * Displays skeleton loaders while the release note is being fetched.
 */
function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-5 w-64" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  )
}

/**
 * Release note detail page component
 *
 * Fetches and displays the full content of a specific release note.
 * This is a public page that does not require authentication.
 */
export function ReleaseNoteDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [releaseNote, setReleaseNote] = useState<ReleaseNote | null>(null)
  const [isLatest, setIsLatest] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchReleaseNote = async () => {
      if (!slug) {
        navigate('/release-notes')
        return
      }

      try {
        const [note, allNotes] = await Promise.all([getReleaseNoteBySlug(slug), getReleaseNotes()])

        if (!note) {
          navigate('/release-notes')
          return
        }

        setReleaseNote(note)
        // Check if this is the latest release
        setIsLatest(allNotes.length > 0 && allNotes[0].slug === slug)
      } catch (error) {
        console.error('Failed to fetch release note:', error)
        navigate('/release-notes')
      } finally {
        setLoading(false)
      }
    }

    fetchReleaseNote()
  }, [slug, navigate])

  return (
    <div className="h-full overflow-y-auto">
      <div className="container max-w-4xl mx-auto p-6">
        {loading ? (
          <LoadingState />
        ) : releaseNote ? (
          <ReleaseNoteDetail releaseNote={releaseNote} isLatest={isLatest} />
        ) : null}
      </div>
    </div>
  )
}
